import debug from 'debug'
import CSREntry from 'src/clientRoutes/_app.news.$id.csr'
import FullSSREntry from 'src/clientRoutes/_app.news.$id.full-ssr'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import { staticMeta, staticMetaLoader } from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app.news.$id'

const log = debug('pixai:ssr-meta')

export const handle = {
  name: 'news-detail',
  track: ({ params }: { params: { id?: string } }) => ({
    scene: 'news',
    section: params.id,
  }),
  showAppNav: true,
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

/**
 * SSR loader for news detail page.
 * Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/ssr/router.ts#L302-L330
 */
export async function loader({ params, request, context }: Route.LoaderArgs) {
  const { attemptParseId } = await import('src/utils/ssr-meta/url.server')
  const { fetchNotificationForSSR } =
    await import('src/utils/ssr-meta/graphql/notification.server')

  // Validate ID format (must be numeric) to avoid unnecessary GraphQL calls
  const id = attemptParseId(params.id)
  if (!id) {
    log('news loader invalid id=%s url=%s', params.id, request.url)
    return {
      ...staticMetaLoader({ requestUrl: request.url }),
      newsApollo: undefined,
    }
  }

  const notification = await fetchNotificationForSSR(id)
  if (!notification) {
    log('news loader not found id=%s url=%s', id, request.url)
    return {
      ...staticMetaLoader({ requestUrl: request.url }),
      newsApollo: undefined,
    }
  }

  const { t, locale } = getI18n(context)
  const locales = notification.locales
  // Use locale from URL, fallback to 'en', then to first available locale
  const localizedData =
    locales[locale] || locales['en'] || Object.values(locales)[0]

  if (!localizedData) {
    log(
      'news loader missing locale data id=%s locale=%s url=%s',
      id,
      locale,
      request.url,
    )
    return {
      ...staticMetaLoader({ requestUrl: request.url }),
      newsApollo: undefined,
    }
  }

  const translatedNewsTitle = t('common:news.title', { context: 'long' })
  const newsTitle =
    translatedNewsTitle && !translatedNewsTitle.startsWith('undefined')
      ? translatedNewsTitle
      : 'News'
  const title = `${newsTitle} | ${localizedData.title}`
  const description = localizedData.content || ''

  const meta = staticMetaLoader({
    requestUrl: request.url,
    title,
    description,
  })

  return {
    ...meta,
    newsApollo: {
      notificationId: id,
      notificationData: {
        notification,
      },
      articleData: localizedData,
    },
  }
}

/** Meta function for SSR SEO. */
export function meta({ loaderData }: Route.MetaArgs) {
  return staticMeta(loaderData)
}

/**
 * Dual-mode entry. `'off'` (CSR) path renders `NewsDetailPage`
 * directly under `<RootRuntime>`'s global Apollo / QueryClient with
 * no SSR seed; `'on'` (Full SSR) path mounts the route-scoped
 * Apollo bridge defined in `clientRoutes/_app.news.$id.full-ssr.tsx`.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <CSREntry />,
  on: () => <FullSSREntry />,
})
